'use client';

import { type MouseEvent, useEffect, useId, useState } from 'react';

export type UseStackedDrawerProps = {
    /** When the host drawer is closed, the stacked drawer should close too. */
    forceClosed?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
    onCloseAll?: () => void;
};

export function useStackedDrawer({ forceClosed = false, onOpenChange, onCloseAll }: UseStackedDrawerProps = {}) {
    const [isActive, setIsActive] = useState(false);
    const [hasBeenRendered, setHasBeenRendered] = useState(false);
    const headingId = useId();

    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    useEffect(() => {
        if (forceClosed) {
            setIsActive(false);
            onOpenChange?.(false);
        }
    }, [forceClosed, onOpenChange]);

    const closeTopDrawer = () => {
        setIsActive(false);
        onOpenChange?.(false);
    };

    const handleOpen = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setIsActive(true);
        onOpenChange?.(true);
    };

    const handleBack = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        closeTopDrawer();
    };

    const handleClose = () => {
        closeTopDrawer();
        onCloseAll?.();
    };

    return {
        isActive,
        hasBeenRendered,
        headingId,
        handleOpen,
        handleBack,
        handleClose,
    };
}
